/**
 * PackageBadge — the Package column's assigned-package pill: colour dot (or
 * package icon), the "edited since applied" pen, the name. Opens the editor
 * when `onEdit` is given; otherwise it is a read-only (disabled) pill.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Package, PenLine } from 'lucide-react';

import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

export interface PackageBadgeProps {
  pkg: NonNullable<OptimizerRow['package']>;
  /** Absent = read-only (no edit rights, or a tracker without the library). */
  onEdit?: () => void;
}

function badgeTitle(pkg: PackageBadgeProps['pkg']): string {
  return pkg.edited
    ? `${pkg.name} — this config was edited after the package was applied; re-apply to sync.`
    : pkg.name;
}

const DOT_CLASS = 'h-2 w-2 shrink-0 rounded-full';

export const PackageBadge: FC<PackageBadgeProps> = ({ pkg, onEdit }) => (
  <button
    type="button"
    disabled={!onEdit}
    onClick={onEdit}
    className="inline-flex max-w-[160px] items-center gap-1.5 rounded-md border border-border/70 bg-muted/30 px-1.5 py-0.5 transition-opacity enabled:hover:opacity-80 disabled:cursor-default"
    aria-label={onEdit ? `Change package (${pkg.name})` : pkg.name}
    title={badgeTitle(pkg)}
  >
    {pkg.color ? (
      <span aria-hidden className={DOT_CLASS} style={{ backgroundColor: pkg.color }} />
    ) : (
      <Package className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    )}
    {pkg.edited ? (
      <PenLine className="h-3 w-3 shrink-0 text-amber-500" aria-label="Edited since applied" />
    ) : null}
    <span className="min-w-0 truncate text-[11px] font-medium">{pkg.name}</span>
  </button>
);

export default PackageBadge;
